import type { AnyObject } from "yup"
import { api } from "./api"
import { useLangRefetch } from "./utils"
import type { LangWrapHook } from "./types"
import type { WithId, components } from "./generated"

export type TaskDefinition = WithId<components["schemas"]["TaskDefinition"]>

const taskDefinitionApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getTaskDefinitions: builder.query<TaskDefinition[], void>({
			query: () => ({
				url: "/taskDefinition",
			}),
			providesTags: (result, error) => {
				if (error || !result) return []
				return [
					...result.map((doc) => ({ type: "TaskDefinition" as const, id: doc._id })),
					{ type: "TaskDefinition" as const, id: "LIST" },
					"Loc" as const,
				]
			},
		}),
		getTaskDefinition: builder.query<TaskDefinition, { id: string; noLoc?: boolean }>({
			query: ({ id, noLoc }) => ({
				url: `/taskDefinition/${id}`,
				params: {
					noLoc,
				},
			}),
			providesTags: (_result, _error, arg) => {
				return [{ type: "TaskDefinition", id: arg.id }]
			},
		}),
		createTaskDefinition: builder.mutation<TaskDefinition, AnyObject>({
			query: (body) => ({
				url: "/taskDefinition",
				method: "POST",
				body,
			}),
			invalidatesTags: () => {
				return [{ type: "TaskDefinition", id: "LIST" }]
			},
		}),
		updateTaskDefinition: builder.mutation<TaskDefinition, { id: string; body: AnyObject }>({
			query: ({ id, body }) => ({
				url: `/taskDefinition/${id}`,
				method: "PUT",
				body,
			}),
			invalidatesTags: (_result, _error, args) => {
				return [
					{ type: "TaskDefinition", id: "LIST" },
					{ type: "TaskDefinition", id: args.id },
				]
			},
		}),
		removeTaskDefinition: builder.mutation<void, string>({
			query: (id) => ({
				url: `/taskDefinition/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: () => {
				return [{ type: "TaskDefinition", id: "LIST" }]
			},
		}),
	}),
})

export const {
	useGetTaskDefinitionsQuery,
	useGetTaskDefinitionQuery,
	useCreateTaskDefinitionMutation,
	useUpdateTaskDefinitionMutation,
	useRemoveTaskDefinitionMutation,
} = taskDefinitionApi

export const useGetTaskDefinitionsQueryL: LangWrapHook<TaskDefinition[]> = (arg, options) =>
	useLangRefetch(useGetTaskDefinitionsQuery, arg, options)
